<script>
export default {
  name: 'ReturnGoods'
}

</script>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from 'axios';
import { modalUtil } from '@functions/modalUtil.js';
import { url } from '@/config/urls';
import { message } from '@functions/message.js';

// 外部定義メソッド
const { showModal } = modalUtil();

// emit定義
const emit = defineEmits([
  'setOperatingGoodsData',
  'updateLoading',
  'countReturnBox',
  'checkDuplicateInReturnBox',
  'addToReturnBox',
  'deleteFromReturnBox',
  'apiError',
  'validationError',
  'select-drug',
  'quantityChanged'
]);

// prop定義
const props = defineProps({
  /**
   * 画面ID
   *    [返品依頼TOP] ReturnSearch
   *    [返品履歴] ReturnHistory
   *    [返品ボックス] ReturnBox
   */
  screenId: {
      type: String,
      required: true
  },
  // 商品情報
  goods: {
    type: Object,
    required: true
  },
  // 商品インデックス
  index: {
    type: Number,
    required: true
  },
  // 商品分類マスタデータ
  goodsTypes: {
    type: Array,
    required: true
  },
  // 返品ボックス更新API URL
  returnBoxUpdateApiUrl: {
    type: String,
    default: url.api.returnBox.update
  },
  fileIconUrl: {
    type: String,
    default: url.static.fileIcon
  },
  // PMDA添付文書URL
  pmdaBaseUrl: {
    type: String,
    default: url.external.pmda
  },
  // ENIF添付文書URL
  enifBaseUrl: {
    type: String,
    default: url.external.enif
  },
  // ログインURL
  loginUrl: {
    type: String,
    default: url.login
  },
  // エラーURL
  errorUrl: {
    type: String,
    default: url.error
  }
});

const elements = {
  chit: ['ReturnSearch', 'ReturnHistory', 'ReturnBox', 'ReturnScan', 'ReturnItemsChoiceModal'], // 伝票No.・伝票日付
  goodsType: ['ReturnSearch', 'ReturnHistory', 'ReturnBox', 'ReturnScan', 'ReturnItemsChoiceModal'], // 商品分類
  makerName: ['ReturnSearch', 'ReturnHistory', 'ReturnBox', 'ReturnScan', 'ReturnItemsChoiceModal'], // メーカー名
  janCode: ['ReturnSearch', 'ReturnHistory', 'ReturnBox', 'ReturnScan', 'ReturnItemsChoiceModal'], // JANコード
  document: ['ReturnSearch', 'ReturnHistory', 'ReturnBox'], // 添付文書
  formalGoodsName: ['ReturnSearch', 'ReturnHistory', 'ReturnBox', 'ReturnScan', 'ReturnItemsChoiceModal'], // 商品名
  lotAndExpirationDate: ['ReturnSearch', 'ReturnHistory', 'ReturnBox', 'ReturnScan', 'ReturnItemsChoiceModal'], // 製造番号(ロット)・使用期限
  deliveryQuantity: ['ReturnSearch', 'ReturnHistory', 'ReturnBox', 'ReturnScan', 'ReturnItemsChoiceModal'], // 納品数
  returnRequestStatus: ['ReturnSearch', 'ReturnHistory', 'ReturnBox', 'ReturnScan', 'ReturnItemsChoiceModal'], // 依頼ステータス
  returnProhibitedReason: ['ReturnSearch', 'ReturnHistory', 'ReturnScan', 'ReturnItemsChoiceModal'], // 返品不可理由
  returnCode: ['ReturnHistory'], // 依頼番号
  comment: ['ReturnHistory'], // コメント
  returnBoxQuantity: ['ReturnSearch', 'ReturnScan', 'ReturnItemsChoiceModal'], // 返品ボックス数量
  returnQuantity: ['ReturnHistory'], // 返品依頼数
  quantityInputDefault: ['ReturnSearch'], // 数量入力欄（通常）
  addToReturnBox: ['ReturnSearch'], // 返品ボックスに追加
  quantityInput: ['ReturnBox', 'ReturnScan'], // 数量入力欄（返品ボックス、返品スキャン）
  selectButton: ['ReturnItemsChoiceModal'], // 選択ボタン
}

// ReturnScan用: 直前の有効な数量を保持
const previousValidQuantity = ref(1);

// 数量inputがフォーカス中かどうか
const isQuantityFocused = ref(false);

// フォーム入力値
const formInputs = ref({
  quantity: 0
});

// エラー
const errors = ref({
  quantity: ''
});

// 数量「＋」
const incrementQuantity = () => {
  // 現在の数量を取得
  let currentQuantity = Number(formInputs.value.quantity);

  // 数値でない場合は 0 にする
  if (isNaN(currentQuantity) || currentQuantity < 0) {
    currentQuantity = 0;
  }

  // +1 して数量を計算
  const quantity = currentQuantity + 1;

  quantityValidation(quantity);
  if (errors.value.quantity !== '' || quantity > 99999) {
    emit('validationError', props.goods.salesDetailsLotId, true);
    return;
  }
  if (props.screenId === 'ReturnScan') {
    previousValidQuantity.value = formInputs.value.quantity;
    emit('quantityChanged', props.goods.salesDetailsLotId, formInputs.value.quantity);
  }
  if (props.screenId === 'ReturnBox') {
    const salesDetailsLotId = props.goods.salesDetailsLotId;
    const quantity = formInputs.value.quantity;
    updateReturnBox(salesDetailsLotId, quantity);
  }
};
// 数量「－」
const decrementQuantity = () => {
  // 現在の数量を取得
  let currentQuantity = Number(formInputs.value.quantity);

  // 数値でない場合は 2 にする ※最小値1とするため
  if (isNaN(currentQuantity) || currentQuantity < 0) {
    currentQuantity = 2;
  }

  // +1 して数量を計算
  const quantity = currentQuantity - 1;

  quantityValidation(quantity);
  if (errors.value.quantity !== '' || quantity <= 0) {
    emit('validationError', props.goods.salesDetailsLotId, true);
    return;
  }
  if (props.screenId === 'ReturnScan') {
    previousValidQuantity.value = formInputs.value.quantity;
    emit('quantityChanged', props.goods.salesDetailsLotId, formInputs.value.quantity);
  }
  if (props.screenId === 'ReturnBox') {
    const salesDetailsLotId = props.goods.salesDetailsLotId;
    const quantity = formInputs.value.quantity;
    updateReturnBox(salesDetailsLotId, quantity);
  }
};
// 数量変更
const onInputQuantity = () => {
  // 現在の数量を取得
  let currentQuantity = Number(formInputs.value.quantity);

  //　入力範囲外の入力は強制的に可能な数字に変換する。空白は免除
  if (formInputs.value.quantity === '') {
    return;
  } else if(isNaN(currentQuantity)) {
    // 数字以外は入力不可
    formInputs.value.quantity = '';
  } else if (formInputs.value.quantity <= 0) {
    // 返品ボックスで入力値が0以下の場合は削除処理を実施
    if (props.screenId === 'ReturnBox') {
        emit('validationError', props.goods.salesDetailsLotId, true);
        onClickDeleteFromReturnBox();
        return;
    }
    // ReturnScan 用
    if (props.screenId === 'ReturnScan') {
      emit('validationError', props.goods.salesDetailsLotId, true, {
        currentQuantity: formInputs.value.quantity,
        previousQuantity: previousValidQuantity.value
      });
      return;
    }
    formInputs.value.quantity = 0;
  } else if (formInputs.value.quantity > getQuantityLimit()) {
    formInputs.value.quantity = getQuantityLimit();
  }

  // バリデーションチェック
  quantityValidation(formInputs.value.quantity);
  if (errors.value.quantity !== '') {
    emit('validationError', props.goods.salesDetailsLotId, true);
    return;
  }
  if (props.screenId === 'ReturnScan') {
    previousValidQuantity.value = formInputs.value.quantity;
    emit('quantityChanged', props.goods.salesDetailsLotId, formInputs.value.quantity);
  }
}
const onChangeQuantity = () => {
  // ReturnScan: 空の場合はフォーカスアウト時に前回有効値へ復元
  if (props.screenId === 'ReturnScan' && formInputs.value.quantity === '') {
    formInputs.value.quantity = previousValidQuantity.value;
    return;
  }
  // 入力確定時のバリデーションチェック
  quantityValidation(formInputs.value.quantity);
  if (errors.value.quantity !== '') {
    emit('validationError', props.goods.salesDetailsLotId, true);
    return;
  }
  if (props.screenId === 'ReturnBox' && formInputs.value.quantity <= 0 || formInputs.value.quantity > getQuantityLimit()) {
    // 返品ボックスで入力値が0以下の場合はNG
    emit('validationError', props.goods.salesDetailsLotId, true);
    let errorMessage = message.error.validation.out_of_range;
    errorMessage = errorMessage.replace('{name}', '数量');
    errorMessage = errorMessage.replace('{min}', '1');
    errorMessage = errorMessage.replace('{max}', getQuantityLimit());
    errors.value.quantity = errorMessage;
    return;
  }
  // 確定時にのみ返品ボックスへ保存する
  if (props.screenId === 'ReturnBox') {
    const salesDetailsLotId = props.goods.salesDetailsLotId;
    const quantity = formInputs.value.quantity;
    updateReturnBox(salesDetailsLotId, quantity);
  }
}
// 数量バリデーション処理
const quantityValidation = (quantity) => {
  errors.value.quantity = '';

  const parsedQuantity = parseInt(quantity);

  // 整数チェック
  if (parsedQuantity !== quantity) {
    let errorMessage = message.error.validation.integer;
    errorMessage = errorMessage.replace('{name}', '数量');
    errors.value.quantity = errorMessage;
  } else if (parsedQuantity < 0 || parsedQuantity > getQuantityLimit()) {
   // 範囲チェック
    let errorMessage = message.error.validation.out_of_range;
    errorMessage = errorMessage.replace('{name}', '数量');
    errorMessage = errorMessage.replace('{min}', '0');
    errorMessage = errorMessage.replace('{max}', getQuantityLimit());
    errors.value.quantity = errorMessage;
  } else {
    // 問題なしなら quantity を整数に変換して返す
    formInputs.value.quantity = parsedQuantity;
    emit('validationError', props.goods.salesDetailsLotId, false);
  }
}

// 数量上限
const getQuantityLimit = () => {
  let limit;
  if (props.screenId === 'ReturnSearch') {
    limit = props.goods.deliveryQuantity - props.goods.returnBoxQuantity;
  } else {
    limit = props.goods.deliveryQuantity;
  }
  return Math.min(limit, 99999);
}

// DateオブジェクトをYYYYMMDD形式に変換
const dateToNumber = (date) => {
  return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
}

// 3ヵ月後の日付をYYYYMMDD形式で取得
const getLimitDate = (dateNumber) => {
  const limitMonths = 3;
  const dateStr = dateNumber.toString();
  if (!/^\d{8}$/.test(dateStr)) return NaN;

  const year = +dateStr.slice(0, 4);
  const month = +dateStr.slice(4, 6) - 1;
  const day = +dateStr.slice(6, 8);

  // 3ヶ月後の「1日」を基準にする
  const base = new Date(year, month + limitMonths, 1);
  // その月の末日を求める
  const lastDay = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  // 同日が存在するなら同日、無ければ末日
  const finalDay = Math.min(day, lastDay);
  return dateToNumber(new Date(base.getFullYear(), base.getMonth(), finalDay));
}

// 3ヵ月経過商品かどうか
const isTimeLimitOver = () => {
  return props.goods.chitDate !== undefined && props.goods.chitDate !== null && getLimitDate(props.goods.chitDate) <= dateToNumber(new Date());
}


// 返品ボックス数量更新
const updateReturnBox = (salesDetailsLotId, quantity) => {
  emit('updateLoading', true);
  const goods ={
    salesDetailsLotId: salesDetailsLotId,
    quantity: quantity
  };
  const param = {
    goodsList: [goods],
  };
  axios.post(props.returnBoxUpdateApiUrl, param)
    .then((response) => {
      emit('countReturnBox');
    })
    .catch((error) => {
      emit('apiError', error);
    })
    .finally(() => {
      emit('updateLoading', false);
    });
}

// モーダル用商品データ（グローバル状態として管理）
const operatingGoodsData = ref(null);

// モーダルに商品データを設定
const setOperatingGoodsData = () => {
  operatingGoodsData.value = {
    salesDetailsLotId: props.goods.salesDetailsLotId,
    quantity: formInputs.value.quantity,
    goodsName: props.goods.formalGoodsName
  };
  emit('setOperatingGoodsData', operatingGoodsData.value);
};

// 「返品ボックスに追加」押下時
const onClickAddToReturnBox = () => {
  // 操作中商品情報の保持
  setOperatingGoodsData();

  if(isTimeLimitOver()) {
    showModal('timeLimitOverConfirmModal');
  } else {
    emit('checkDuplicateInReturnBox');
  }

  // 数量をリセット
  formInputs.value.quantity = 0;
};

// 返品ボックス 削除確認
const onClickDeleteFromReturnBox = () => {
  // ReturnScanの場合はスキャン削除確認モーダルを呼ぶ
  if (props.screenId === 'ReturnScan') {
    emit('validationError', props.goods.salesDetailsLotId, true, {
      currentQuantity: 0
      , previousQuantity: formInputs.value.quantity
    });
    return;
  }

  // 操作中商品情報の保持
  setOperatingGoodsData();

  // キーボードの裏にモーダルが隠れないよう、開く前にフォーカスを外しておく
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }

  // 削除確認モーダルを表示
  showModal('deleteFromReturnBoxConfirmModal');
}

// 商品分類名
const goodsTypeName = computed(() => {
  if (!props.goods.goodsType) {
    return '';
  }

  const goodsType = props.goodsTypes.find(
    item => item.code === props.goods.goodsType
  );

  return goodsType?.name || '';
});

// 伝票番号（5桁で左ゼロ埋め）
const formattedChitNumber = computed(() => {
  if (props.goods.chitNumber !== undefined && props.goods.chitNumber !== null) {
    return String(props.goods.chitNumber).padStart(5, '0');
  }
  return '';
});

const onClickSelectDrug = () => {
  emit('select-drug', props.goods);
}

// 数量の設定
const setParams = () => {
  if(props.screenId === 'ReturnSearch') {
    // 返品依頼TOP 初期値0
    formInputs.value.quantity = 0;
  } else if (props.screenId === 'ReturnBox') {
    // 返品ボックス
    formInputs.value.quantity = props.goods.returnBoxQuantity;
  } else if (props.screenId === 'ReturnScan') {
    formInputs.value.quantity = props.goods.quantity ?? 1;
    previousValidQuantity.value = formInputs.value.quantity;
  }
}

onMounted(() => {
  // 数量の初期値設定
  setParams();
});

watch(
  () => props.goods?.quantity,
  (newQty) => {
    if (!isQuantityFocused.value) {
      formInputs.value.quantity = newQty;
      if (props.screenId === 'ReturnScan') {
        formInputs.value.quantity = newQty ?? 1;
        previousValidQuantity.value = formInputs.value.quantity;
      }
    }
  }
);

// 商品の削除キャンセル後にgoodsの参照変化を検知して画面に反映する
watch(
  () => props.goods,
  (newGoods) => {
    if (props.screenId === 'ReturnScan') {
      formInputs.value.quantity = newGoods?.quantity ?? 1;
      previousValidQuantity.value = formInputs.value.quantity;
    }
  },
  { deep: true }
);

</script>

<template>
  <div class="simple_card st2" :class="{ cl1: props.goods.canReturn !== undefined && !props.goods.canReturn }">
    <div class="simple_card_body">
      <div class="simple_card_body_top_content1">
        <div class="body-top-content">
          <div class="body-top-content-left">
            <div class="com_comment1_container">
              <p class="com_fs16 com_color1 nowrap" v-if="elements.returnCode.includes(props.screenId)">依頼番号：{{ props.goods.returnCode }}</p>
              <span class="com_comment1" v-if="elements.comment.includes(props.screenId) && props.goods.comment !== undefined && props.goods.comment !== null && props.goods.comment !== '' ">
                <img src="/front/assets/common/img/icon_comment1.png" alt="">{{ props.goods.comment }}
              </span>
            </div>
            <p class="com_fs14" v-if="elements.chit.includes(props.screenId)">{{ formattedChitNumber }}-{{ props.goods.chitDate }}</p>
          </div>
          <div class="body-top-content-right" v-if="elements.returnRequestStatus.includes(props.screenId)">
            <template v-if="props.goods.returnProhibitedReasonCode !== undefined && props.goods.returnProhibitedReasonCode !== null && props.goods.returnProhibitedReasonCode !== '' ">
              <div class="com_status2 d">返品不可商品<img src="/front/assets/common/img/icon_s_nra1.png" alt="" class="icon_s_nra1"></div>
            </template>
            <template v-else-if="props.screenId !== 'ReturnHistory' && isTimeLimitOver()">
              <div class="com_status2 d">3ヵ月経過品</div>
            </template>
            <template v-else-if="props.goods.returnRequestStatus !== undefined && props.goods.returnRequestStatus !== null && props.goods.returnRequestStatus !== ''">
              <div class="com_status2 c" v-if="props.goods.returnRequestStatus === '01'">依頼中</div>
              <div class="com_status2 e" v-else-if="props.goods.returnRequestStatus === '02'">確認中</div>
              <div class="com_status2 a" v-else-if="props.goods.returnRequestStatus === '03'">返品：◯</div>
              <div class="com_status2 b" v-else-if="props.goods.returnRequestStatus === '04'">返品：×</div>
              <div class="com_status2 f" v-else-if="props.goods.returnRequestStatus === '05'">返品：△</div>
            </template>
          </div>
        </div>
        <div class="com_right1" v-if="elements.returnRequestStatus.includes(props.screenId)">
          <template v-if="props.goods.returnProhibitedReasonCode !== undefined && props.goods.returnProhibitedReasonCode !== null && props.goods.returnProhibitedReasonCode !== '' ">
            <div class="com_fs12 com_mgt5">{{ props.goods.returnProhibitedReason }}</div>
          </template>
        </div>
      </div>
      <div class="simple_card_body_content">
        <div class="body-upper-half">
          <div class="grid_container">
            <p class="simple_txt_16 simple_blue_bold" v-if="elements.goodsType.includes(props.screenId)">{{ goodsTypeName }}</p>
            <p class="simple_txt_14">
              <span v-if="elements.janCode.includes(props.screenId)">
                {{ props.goods.janCode }}
              </span>
              &emsp;
              <a v-if="elements.document.includes(props.screenId) && props.goods.goodsType != '04' && props.goods.salePackagingUnitCode != null && props.goods.salePackagingUnitCode != ''"
                  target="_blank" class="com_link2 b" :href="props.pmdaBaseUrl.replace('{GTIN}', props.goods.salePackagingUnitCode)">
                添付文書<img :src="props.fileIconUrl" alt="PDF" class="com_icon_pdf1" />
              </a>
            </p>
          </div>
          <div class="grid_container c3">
            <p class="simple_txt_18">
              <span class="simple_txt_ln1" v-if="elements.makerName.includes(props.screenId)">
                {{ props.goods.makerName }}
              </span>
            </p>
            <p class="simple_txt_18" v-if="elements.formalGoodsName.includes(props.screenId)">
              {{ props.goods.formalGoodsName }}
            </p>
            <div class="data1" v-if="elements.lotAndExpirationDate.includes(props.screenId)">
              <p class="com_fs12 color1">製造番号/使用期限</p>
              <p class="com_fs14" v-if="props.goods.lot !== null && props.goods.lot !== ''">{{ props.goods.lot }}</p>
              <p class="com_fs14" v-if="props.goods.expirationDate1 !== null && props.goods.expirationDate1 !== ''">{{ props.goods.expirationDate1 }}</p>
            </div>
          </div>
        </div>
        <div class="body_lower_half">
          <div class="txt_box1" :class="{ 'mgt-a com_mgb0': elements.returnQuantity.includes(props.screenId) }">
            <template v-if="elements.deliveryQuantity.includes(props.screenId)">
              <span class="com_fs14" :class="{ 'com_mgb5': elements.returnQuantity.includes(props.screenId) }">納品数</span> <span class="com_fs18">{{ props.goods.deliveryQuantity }}</span>
            </template>
            <template v-if="elements.returnQuantity.includes(props.screenId)">
              <br><span class="com_fs14">返品依頼数</span> <span class="com_fs18 com_color3">{{ props.goods.returnQuantity }}</span>
            </template>
          </div>
          <div class="txt_box2" v-if="elements.returnBoxQuantity.includes(props.screenId) && props.goods.returnBoxQuantity > 0">
            <span class="com_fs10 com_strong1 com_color1">返品ボックス内：{{ props.goods.returnBoxQuantity }}</span>
          </div>
          <div class="com_ipt_wrap1">
            <div v-if="elements.quantityInputDefault.includes(props.screenId)" class="com_count_group1">
              <button type="button" class="com_btn8 l" @click="decrementQuantity()" :disabled="!props.goods.canReturn || formInputs.quantity === 0"><span>−</span></button>
              <input type="number" inputmode="numeric" v-model="formInputs.quantity" @input="onInputQuantity" @change="onChangeQuantity" class="form-control com_form1"
                    :class="{ error: errors.quantity !== '' }" min="1" :max="getQuantityLimit()" :disabled="!props.goods.canReturn">
              <button type="button" class="com_btn8 r" @click="incrementQuantity()" :disabled="!props.goods.canReturn || formInputs.quantity >= getQuantityLimit()"><span>＋</span></button>
            </div>
            <button v-if="elements.addToReturnBox.includes(props.screenId)" type="button" @click="onClickAddToReturnBox" class="com_btn9" :disabled="!props.goods.canReturn || formInputs.quantity === 0 || errors.quantity !== ''">
              <span>返品ボックスに<span>追加</span></span>
            </button>
            <div v-if="elements.quantityInput.includes(props.screenId)" class="com_count_group1">
              <button v-if="!props.goods.canReturn || formInputs.quantity === 1" type="button" class="com_btn8 l cl1" @click="onClickDeleteFromReturnBox"><span><img src="/front/assets/common/img/icon_trash2.png" alt="" class="icon_trash2"></span></button>
              <button v-else type="button" class="com_btn8 l" @click="decrementQuantity()" :disabled="formInputs.quantity === 0"><span>−</span></button>
              <input type="number" inputmode="numeric" v-model="formInputs.quantity" @focus="isQuantityFocused = true" @blur="isQuantityFocused = false; onChangeQuantity()" @input="onInputQuantity" class="form-control com_form1"
                    :class="{ error: errors.quantity !== '' }" min="1" :max="getQuantityLimit()" :disabled="!props.goods.canReturn">
              <button type="button" class="com_btn8 r" @click="incrementQuantity()" :disabled="!props.goods.canReturn || formInputs.quantity >= getQuantityLimit()"><span>＋</span></button>
            </div>
          </div>
          <div v-if="elements.selectButton.includes(props.screenId)" class="select-btn-wrap">
            <a
              href=""
              class="com_app_btn3"
              :class="{ disabled: !props.goods.canReturn }"
              :data-bs-dismiss="props.goods.canReturn ? 'modal' : null"
              :aria-disabled="!props.goods.canReturn"
              @click.prevent="props.goods.canReturn && onClickSelectDrug()"
            >
              選択
            </a>
          </div>
        </div>
      </div>
      <div v-if="errors.quantity !== ''" class="simple_card_error1">
        <span class="com_fs12 com_strong1 com_color4">
          {{ errors.quantity }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>

.nowrap {
  white-space: nowrap;
}

.body-top-content {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.body-top-content >.body-top-content-left {
  flex: 1;
  min-width: 0;
}

.body-top-content >.body-top-content-right {
  margin-left: auto;
  flex-shrink: 0;
}
.com_comment1_container {
  display: flex;
  flex-wrap: wrap;
}


@media only screen and (min-width: 822px) {
  /* PC版レイアウト */
  .com_comment1_container {
    min-width: 0;
  }
}

@media only screen and (max-width: 821px) {
  /* スマホ版レイアウト */
  .com_comment1 {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }
}

</style>
